import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Paginator } from 'primereact/paginator';
import { LoadingService } from '../../../AppLoading';
import { ToastService } from '../../../AppToast';
import { InvoiceService as Service } from '../InvoiceService';
import EventEmitter from '../../../utils/EventEmitter';
import moment from 'moment';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputNumber } from 'primereact/inputnumber';
import { FormatDisplay } from '../../../shared/format';
import { PrintConfigService } from '../PrintConfigService';
import { useInvoicePaymentStatusPolling } from '../hooks/useInvoicePaymentStatusPolling';
import { useQrPaymentSettings } from '../hooks/useQrPaymentInvoiceEnabled';
import { useSinhSoSauInvoiceEnabled } from '../hooks/useSinhSoSauInvoiceEnabled';
import { useTranslation } from '../../../shared/language';

interface DialogViewProps {
  display: boolean;
  selectedItem: any;
  setDisplay: (value: boolean) => void;
  setDisplayCancel?: (value: boolean) => void;
  setDisplayCrup?: (value: boolean) => void;
  setInvoiceStatus?: (status: number) => void;
  setDisplaySendEmail?: (value: boolean) => void;
  // invoiceContext giữ thông tin nghiệp vụ khi params chỉ chứa ID hóa đơn.
  sign?: (params: any, invoiceContext?: any) => void;
  setSelectedItems?: (value: any) => void;
  showMinimalToolbar?: boolean;
}

const DialogView = ({ display, selectedItem, setDisplay, setDisplayCrup, setInvoiceStatus, setDisplaySendEmail, sign, setSelectedItems, showMinimalToolbar }: DialogViewProps) => {
  const { t } = useTranslation();
  const [previewHeight, setPreviewHeight] = useState(500);
  const [basicFirst, setBasicFirst] = useState(0);
  const [basicRows, setBasicRows] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [canSign, setCanSign] = useState(false);
  const [formPrintConfig, setFormPrintConfig] = useState({
    numberRecordMutile: null,
    numberRecord: null,
  })
  const [, setKeyReloadQRCode] = useState<string | null>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);
  const menuRef = useRef<any>(null);
  const pageHtmlRef = useRef<string[]>([]);
  const fullPageHtmlRef = useRef<string>('');
  const opRef = useRef<any>(null);
  const lastPaymentToastInvoiceIdRef = useRef<string | null>(null);
  const wasPaidOnOpenRef = useRef(false);
  const openedInvoiceIdRef = useRef<string | null>(null);
  const prevPaymentCompletedRef = useRef(false);
  const initialPaymentResolvedRef = useRef(false);
  const [isPrintViewLoaded, setIsPrintViewLoaded] = useState(false);

  const { isQrPaymentEnabled, isCreateQrForDraftEnabled } = useQrPaymentSettings(display);
  const isSinhSoSauEnabled = useSinhSoSauInvoiceEnabled(
    selectedItem?.invoiceSerial,
  );
  const isDraftInvoice = selectedItem?.sendTaxStatus === 1;
  const isQrDraftEnabled = !isDraftInvoice || isCreateQrForDraftEnabled;

  const isPaymentFeatureEnabled = isQrPaymentEnabled && isQrDraftEnabled;
  const isDerivativeInvoice = Service.requiresOwnPayment(
    selectedItem?.invoiceStatus,
  );
  const listIsPaymented = Boolean(selectedItem?.isPaymented);
  const effectiveListIsPaymented = isDerivativeInvoice
    ? false
    : listIsPaymented;

  const isPaymentStatusNull =
    selectedItem?.paymentStatus === null ||
    selectedItem?.paymentStatus === undefined ||
    selectedItem?.paymentStatus === 0;

  const shouldPollPaymentStatus =
    display &&
    isPrintViewLoaded &&
    isQrPaymentEnabled &&
    isQrDraftEnabled &&
    !effectiveListIsPaymented &&
    isPaymentStatusNull;

  const paymentInfo = useInvoicePaymentStatusPolling({
    invoiceId: selectedItem?.id,
    enabled: shouldPollPaymentStatus,
    isPaymented: effectiveListIsPaymented,
  });

  const isPaymentCompleted = Service.resolveIsPaymented(
    {
      invoiceStatus: selectedItem?.invoiceStatus,
      isPaymented: listIsPaymented,
    },
    shouldPollPaymentStatus ? paymentInfo?.isPaymented : undefined,
  );

  const shouldShowPaymentStatus =
    isPaymentFeatureEnabled || isPaymentCompleted;

  const displayPaidAmount = isDerivativeInvoice
    ? (paymentInfo?.paidAmount ?? null)
    : (paymentInfo?.paidAmount ?? selectedItem?.paidAmount ?? null);

  const displayPaymentTime = isDerivativeInvoice
    ? (paymentInfo?.paymentTime ?? null)
    : (paymentInfo?.paymentTime ?? selectedItem?.paymentTime ?? null);

  const getPaymentSuccessMessage = (
    isSinhSoSau: boolean,
    invoiceSerial?: string,
    invoiceNumber?: string | number | null,
  ) => {
    if (isSinhSoSau) {
      return t('common.paymentCompleted');
    }

    const hasInvoiceNumber =
      invoiceNumber !== null &&
      invoiceNumber !== undefined &&
      invoiceNumber !== '';

    if (hasInvoiceNumber) {
      return t('common.paymentCompletedWithNumber', {serial: invoiceSerial, number: invoiceNumber});
    }

    return t('common.paymentCompleted');
  };

  useEffect(() => {
    if (!display) {
      wasPaidOnOpenRef.current = false;
      lastPaymentToastInvoiceIdRef.current = null;
      openedInvoiceIdRef.current = null;
      prevPaymentCompletedRef.current = false;
      initialPaymentResolvedRef.current = false;
      setIsPrintViewLoaded(false);
      return;
    }

    if (openedInvoiceIdRef.current !== selectedItem?.id) {
      openedInvoiceIdRef.current = selectedItem?.id ?? null;
      lastPaymentToastInvoiceIdRef.current = null;
      initialPaymentResolvedRef.current = effectiveListIsPaymented;
      setIsPrintViewLoaded(false);

      if (effectiveListIsPaymented) {
        wasPaidOnOpenRef.current = true;
        prevPaymentCompletedRef.current = true;
      } else {
        wasPaidOnOpenRef.current = false;
        prevPaymentCompletedRef.current = false;
      }
    }
  }, [display, effectiveListIsPaymented, selectedItem?.id]);

  useEffect(() => {
    if (
      !display ||
      !selectedItem?.id ||
      initialPaymentResolvedRef.current ||
      !shouldPollPaymentStatus ||
      paymentInfo === null
    ) {
      return;
    }

    initialPaymentResolvedRef.current = true;

    if (paymentInfo.isPaymented) {
      wasPaidOnOpenRef.current = true;
      prevPaymentCompletedRef.current = true;
      return;
    }

    wasPaidOnOpenRef.current = false;
    prevPaymentCompletedRef.current = false;
  }, [
    display,
    paymentInfo,
    selectedItem?.id,
    shouldPollPaymentStatus,
  ]);

  useEffect(() => {
    if (!display || !selectedItem?.id) {
      return;
    }

    if (shouldPollPaymentStatus && !initialPaymentResolvedRef.current) {
      return;
    }

    const justPaidDuringView =
      isPaymentCompleted && !prevPaymentCompletedRef.current;

    prevPaymentCompletedRef.current = isPaymentCompleted;

    if (
      !justPaidDuringView ||
      wasPaidOnOpenRef.current ||
      lastPaymentToastInvoiceIdRef.current === selectedItem.id
    ) {
      return;
    }

    lastPaymentToastInvoiceIdRef.current = selectedItem.id;

    ToastService.setMessage({
      severity: 'success',
      summary: t('common.success'),
      detail: getPaymentSuccessMessage(
        isSinhSoSauEnabled,
        selectedItem?.invoiceSerial,
        selectedItem?.invoiceNumber,
      ),
      life: 5000,
    });
  }, [
    display,
    isPaymentCompleted,
    isSinhSoSauEnabled,
    selectedItem?.id,
    selectedItem?.invoiceNumber,
    selectedItem?.invoiceSerial,
    shouldPollPaymentStatus,
  ]);

  // useEffect(() => {
  //   if (selectedItem?.id) {
  //     PrintConfigService.get(selectedItem.id).then(res => setFormPrintConfig({
  //       numberRecord: res.numberRecord,
  //       numberRecordMutile: res.numberRecordMutile,
  //     }));
 
  //   }
  // }, [selectedItem?.id])

  const onShow = useCallback(() => {
    if (display && selectedItem?.id) {
      let signAllowed = selectedItem.sendTaxStatus === 1;

      setCanSign(signAllowed);
      setIsPrintViewLoaded(false);

      Service.printViewHtml(selectedItem.id).then((data) => {
        if (data.keyReloadQRCode) {
          setKeyReloadQRCode(data.keyReloadQRCode);
        }
        setKeyReloadQRCode(data.keyReloadQRCode);
        if (data.pagehmtl) {
          pageHtmlRef.current = data.pagehmtl;

          setPreviewHeight(data.pageSize === 'A5' ? 610 : 1200);
          setTotalRecords(data.pagehmtl.length);

          if (previewRef.current) {
            previewRef.current.contentWindow?.document.open();
            previewRef.current.contentWindow?.document.write(data.pagehmtl[0].trim());
          }

          fullPageHtmlRef.current = data.pagehmtl.map((page: string) => page.trim()).join('');
        }
      }).finally(() => {
        setIsPrintViewLoaded(true);
      });
    }
  }, [display, selectedItem]);

  useEffect(() => {
    const reloadData = () => {
      LoadingService.start();
      onShow();
      LoadingService.stop();
    };

    const eventListener = EventEmitter.addListener('reloadSearchInvoiceSign', reloadData);

    return () => {
      eventListener.removeListener('reloadSearchInvoiceSign', reloadData);
    };
  }, [onShow]);

  const print = () => {
    const myWindow = window.open('') as Window;
    myWindow.document.open('text/plain');
    myWindow.document.write(fullPageHtmlRef.current);
    myWindow.document.close();
    myWindow.onafterprint = () => myWindow.close();
    myWindow.print();
  };

  const printSwitch = () => {
    if (selectedItem?.id) {
      Service.printViewSwitchHtml(selectedItem.id).then((data) => {
        if (data.pagehmtl) {
          const html = data.pagehmtl.map((page: string) => page.trim()).join('');

          const myWindow = window.open('') as Window;
          myWindow.document.open('text/plain');
          myWindow.document.write(html);
          myWindow.document.close();
          myWindow.onafterprint = () => myWindow.close();
          myWindow.print();
        }
      });
    }
  };

  // Truyền selectedItem để luồng ký có thể nhận diện hóa đơn kèm bảng kê.
  const signInvoice = () => {
    if (selectedItem?.id) {
      sign(
        { CommandName: "SignInvoices", InvoiceIdArray: [selectedItem.id] },
        selectedItem,
      );
    }
  };

  const sendEmail = () => {
    EventEmitter.emit("showDialogSendEmail", selectedItem);
    setDisplaySendEmail?.(true);
  };

  const downloadPdf = () => {
    if (selectedItem?.id) {
      Service.downloadInvoicePdf(selectedItem.id).then((data) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([data]));

        const fileName = selectedItem.buyerTaxCode
          ? `${selectedItem.sellerTaxCode}_${selectedItem.invoiceSerial}_${selectedItem.invoiceNumber ? selectedItem.invoiceNumber : ""}_${selectedItem.buyerTaxCode}_${selectedItem.invoiceDate ? FormatDisplay.date(selectedItem.invoiceDate, 'DD-MM-YYYY') : moment().format('DD-MM-YYYY')}.pdf`
          : `${selectedItem.sellerTaxCode}_${selectedItem.invoiceSerial}_${selectedItem.invoiceNumber ? selectedItem.invoiceNumber : ""}_${selectedItem.invoiceDate ? FormatDisplay.date(selectedItem.invoiceDate, 'DD-MM-YYYY') : moment().format('DD-MM-YYYY')}.pdf`;

        link.download = fileName;
        link.click();
      });
    }
  };

  const onBasicPageChange = (event: any) => {
    setBasicFirst(event.first);
    setBasicRows(event.rows);

    if (previewRef.current) {
      previewRef.current.contentWindow?.document.open();
      previewRef.current.contentWindow?.document.write(pageHtmlRef.current[event.first].trim());
    }
  };

  const handleUpdatePrintConfig = () => {
    PrintConfigService.create({
      ...formPrintConfig,
      invoiceId: selectedItem.id,
    }).then((result) => {
      onShow()
    })
  }

  return (
    <Dialog
      visible={display}
      style={{ width: '890px' }}
      draggable={false}
      resizable
      position="top"
      onHide={() => {
        setDisplay(false)
        setSelectedItems?.(null)
        setKeyReloadQRCode(null)
      }}
      onShow={onShow}
      className="view-invoice"
      icons={
        showMinimalToolbar ? (
          <div className="mr-2 flex justify-content-end">
            <Button label={t('invoice.printInvoice')} icon="pi pi-print" onClick={print} className="p-button-outlined p-button-sm mr-2" />
            <Button label={t('invoice.downloadPdf')} icon="pi pi-file-pdf" onClick={downloadPdf} className="p-button-outlined p-button-sm mr-2" />
          </div>
        ) : (
        <div className="mr-2 grid">
          <div className="col-fixed p-0 pt-2 mr-3" style={{ width: '300px' }}>
            <Paginator first={basicFirst} rows={basicRows} totalRecords={totalRecords} onPageChange={onBasicPageChange} pageLinkSize={3} />

          </div>
          <div className="col-fixed p-0 pt-3 text-right" style={{ width: '510px' }}>
            <Button label={t('common.settings')} icon="pi pi-cog" onClick={(e) => opRef.current.toggle(e)} className="p-button-outlined p-button-sm mr-2" />
            <OverlayPanel
              ref={opRef}
              showCloseIcon={false}
              id="overlay_panel"
              style={{ width: '450px' }}
              className="overlaypanel-demo"
              onShow={() => {
                PrintConfigService.get(selectedItem.id).then(res => setFormPrintConfig({
                  numberRecord: res.numberRecord,
                  numberRecordMutile: res.numberRecordMutile,
                }));
              }}

            >
              <div className="field grid">
                <label className="col-12 mb-2 md:col-6 md:mb-0">{t('common.blankLines')}</label>
                <div className="col-12 md:col-6">
                  <InputNumber locale={FormatDisplay.locales} className="p-inputtext-sm" showButtons value={formPrintConfig.numberRecord} onChange={(e) => setFormPrintConfig({ ...formPrintConfig, numberRecord: e.value })} />
                </div>
              </div>
              <div className="field grid">
                <label className="col-12 mb-2 md:col-6 md:mb-0">{t('common.linesPerPage')}</label>
                <div className="col-12 md:col-6">
                  <InputNumber locale={FormatDisplay.locales} className="p-inputtext-sm" showButtons value={formPrintConfig.numberRecordMutile} onChange={(e) => setFormPrintConfig({ ...formPrintConfig, numberRecordMutile: e.value })} />
                </div>
              </div>
              <div className="flex justify-content-center">
                <Button label={t('common.save')} className="p-button-outlined p-button-sm" onClick={handleUpdatePrintConfig} />
              </div>
            </OverlayPanel>
            {canSign && <Button label={t('invoice.signToTaxAuthority')} icon="pi pi-print" onClick={signInvoice} className="p-button-outlined p-button-sm mr-2" />}
            <Button label={t('invoice.printInvoice')} icon="pi pi-print" onClick={print} className="p-button-outlined p-button-sm mr-2" />
            <Button label={t('invoice.downloadPdf')} icon="pi pi-file-pdf" onClick={downloadPdf} className="p-button-outlined p-button-sm mr-2" />
            <Menu model={[
              {
                label: t('invoice.printSwitch'),
                icon: 'pi pi-print',
                command: printSwitch
              },
              {
                label: t('common.sendEmail'),
                icon: 'pi pi-envelope',
                command: sendEmail
              }
            ]} popup ref={menuRef} id="popup_menu" />
            <Button className="p-button-outlined p-button-sm mr-2" label={t('common.functions')} icon="pi pi-th-large" onClick={(event) => menuRef.current.toggle(event)} aria-controls="popup_menu" aria-haspopup />
            {/* <Button label="Chuyển đổi" icon="pi pi-print" onClick={printSwitch} className="p-button-outlined p-button-sm mr-2" />
            <Button label="Gửi email" icon="pi pi-envelope" onClick={sendEmail} className="p-button-outlined p-button-sm mr-2" /> */}
          </div>
        </div>
        )
      }
    >
      <div className="mt-3">
        {shouldShowPaymentStatus && isPrintViewLoaded && (
          <div className="flex justify-content-end mb-3">
            {isPaymentCompleted ? (
              <span className="flex align-items-center gap-2 text-green-600 font-semibold">
                <i className="pi pi-check" />
                <p className="m-0">
                  {t('common.paymentCompleted')}
                  {displayPaidAmount != null
                    ? `: ${FormatDisplay.number(displayPaidAmount)} VND`
                    : ''}
                  {displayPaymentTime
                    ? ` - ${moment(displayPaymentTime).format('HH:mm:ss DD/MM/YYYY')}`
                    : ''}
                </p>
              </span>
            ) : (
              <span className="flex align-items-center gap-2 text-yellow-500 font-semibold">
                <i className="pi pi-money-bill" />
                <p className="m-0">{t('common.paymentPending')}</p>
              </span>
            )}
          </div>
        )}
        <iframe
          className="template-preview"
          ref={previewRef}
          title="template-preview"
          style={{ height: `${previewHeight}px` }}
        />
      </div>
    </Dialog>
  );  
};

export { DialogView };
